import { useState, useCallback, useEffect } from 'react';
import { horseNamingService } from '../services/horseNamingService';
import { HorseNamingRequest, HorseNamingResponse, HorseNameSuggestion, ApiProvider, UsageStats } from '../types/api';

interface UseHorseNamingResult {
  suggestions: HorseNameSuggestion[] | null;
  loading: boolean;
  error: string | null;
  responseTime: number | null;
  usageStats: UsageStats[] | null;
  usageLoading: boolean;
  usageError: string | null;
  generateNames: (request: HorseNamingRequest, apiProvider: ApiProvider) => Promise<void>;
  refreshUsageStats: () => Promise<void>;
}

export const useHorseNaming = (): UseHorseNamingResult => {
  const [suggestions, setSuggestions] = useState<HorseNameSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats[] | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const generateNames = useCallback(async (request: HorseNamingRequest, apiProvider: ApiProvider) => {
    try {
      setLoading(true);
      setError(null);
      setSuggestions(null);
      setResponseTime(null);

      const startTime = Date.now();
      const response: HorseNamingResponse = await horseNamingService.generateNames(request, apiProvider);
      const endTime = Date.now();
      const timeTaken = endTime - startTime;

      if (response.success && response.suggestions) {
        setSuggestions(response.suggestions);
        setResponseTime(timeTaken);
      } else {
        setError(response.error || 'Failed to generate names');
        setSuggestions(null);
        setResponseTime(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate names');
      setSuggestions(null);
      setResponseTime(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUsageStats = useCallback(async () => {
    try {
      setUsageLoading(true);
      setUsageError(null);

      const response = await horseNamingService.getUsageStats();
      if (response.success) {
        setUsageStats(response.usageStats);
      } else {
        setUsageError('Failed to fetch usage statistics');
      }
    } catch (err) {
      setUsageError(err instanceof Error ? err.message : 'Failed to fetch usage statistics');
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUsageStats();
  }, [refreshUsageStats]);

  return {
    suggestions,
    loading,
    error,
    responseTime,
    usageStats,
    usageLoading,
    usageError,
    generateNames,
    refreshUsageStats,
  };
};